import React, { useEffect, useMemo, useState } from "react";
import { Badge, Button, Card, CardBody, Col, Container, Input, Label, Row } from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { deleteFerrailleur, deleteGroupe, loadFerrailleurs, loadGroupes, saveFerrailleur, saveGroupe } from "./reseauService";

const emptyGroupe = { nom: "", categorie: "A", description: "", actif: true };
const emptyFerrailleur = { nom: "", raisonSociale: "", ice: "", telephone: "", email: "", ville: "", actif: true, groupeIds: [] };

function ReseauPage() {
  const [groupes, setGroupes] = useState([]);
  const [ferrailleurs, setFerrailleurs] = useState([]);
  const [groupeForm, setGroupeForm] = useState(emptyGroupe);
  const [ferrailleurForm, setFerrailleurForm] = useState(emptyFerrailleur);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    const [groupesResponse, ferrailleursResponse] = await Promise.all([loadGroupes(), loadFerrailleurs()]);
    setGroupes(groupesResponse?.data?.body || []);
    setFerrailleurs(ferrailleursResponse?.data?.body || []);
  };

  const saveGroupeForm = async (event) => {
    event.preventDefault();
    await saveGroupe(groupeForm);
    setGroupeForm(emptyGroupe);
    loadData();
  };

  const saveFerrailleurForm = async (event) => {
    event.preventDefault();
    await saveFerrailleur(ferrailleurForm);
    setFerrailleurForm(emptyFerrailleur);
    loadData();
  };

  const toggleGroupForFerrailleur = (id) => {
    setFerrailleurForm((prev) => {
      const current = prev.groupeIds || [];
      return { ...prev, groupeIds: current.includes(id) ? current.filter((item) => item !== id) : [...current, id] };
    });
  };

  const counts = useMemo(() => ({
    groupes: groupes.length,
    ferrailleurs: ferrailleurs.length,
    actifs: ferrailleurs.filter((item) => item.actif).length,
  }), [groupes, ferrailleurs]);

  return (
    <div className="page-content auction-page">
      <Container fluid>
        <Breadcrumbs title="Réseau" breadcrumbItems={[{ title: "Modules", link: "#" }, { title: "Réseau", link: "#" }]} />
        <Row className="g-3 mb-4">
          <Col md={4}><Card><CardBody><span className="text-muted">Groupes</span><div className="fs-3 fw-semibold">{counts.groupes}</div></CardBody></Card></Col>
          <Col md={4}><Card><CardBody><span className="text-muted">Férailleurs</span><div className="fs-3 fw-semibold">{counts.ferrailleurs}</div></CardBody></Card></Col>
          <Col md={4}><Card><CardBody><span className="text-muted">Actifs</span><div className="fs-3 fw-semibold">{counts.actifs}</div></CardBody></Card></Col>
        </Row>

        <Row className="g-4">
          <Col xl={5}>
            <Card className="mb-4">
              <CardBody>
                <h4 className="mb-3">{groupeForm.id ? "Modifier un groupe" : "Créer un groupe"}</h4>
                <form onSubmit={saveGroupeForm}>
                  <Row className="g-3">
                    <Col md={8}><Label>Nom</Label><Input required value={groupeForm.nom} onChange={(e) => setGroupeForm({ ...groupeForm, nom: e.target.value })} /></Col>
                    <Col md={4}>
                      <Label>Categorie</Label>
                      <Input type="select" value={groupeForm.categorie} onChange={(e) => setGroupeForm({ ...groupeForm, categorie: e.target.value })}>
                        {["A", "B", "C", "TOUS"].map((item) => <option key={item}>{item}</option>)}
                      </Input>
                    </Col>
                    <Col xs={12}><Label>Description</Label><Input value={groupeForm.description || ""} onChange={(e) => setGroupeForm({ ...groupeForm, description: e.target.value })} /></Col>
                    <Col xs={12}><Button color="primary" type="submit">Enregistrer groupe</Button></Col>
                  </Row>
                </form>
              </CardBody>
            </Card>

            <Card>
              <CardBody>
                <h4 className="mb-3">Groupes</h4>
                <div className="stack-list">
                  {groupes.map((groupe) => (
                    <div className="stack-row" key={groupe.id}>
                      <div>
                        <strong>{groupe.nom}</strong>
                        <span>{groupe.description || "Aucune description"}</span>
                      </div>
                      <div className="d-flex gap-2 align-items-center">
                        <Badge color="info">{groupe.categorie}</Badge>
                        <Button size="sm" color="light" onClick={() => setGroupeForm(groupe)}>Modifier</Button>
                        <Button size="sm" color="light" className="text-danger" onClick={async () => { await deleteGroupe(groupe.id); loadData(); }}>Supprimer</Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col xl={7}>
            <Card className="mb-4">
              <CardBody>
                <h4 className="mb-3">{ferrailleurForm.id ? "Modifier un férailleur" : "Créer un férailleur"}</h4>
                <form onSubmit={saveFerrailleurForm}>
                  <Row className="g-3">
                    <Col md={6}><Label>Nom</Label><Input required value={ferrailleurForm.nom} onChange={(e) => setFerrailleurForm({ ...ferrailleurForm, nom: e.target.value })} /></Col>
                    <Col md={6}><Label>Raison sociale</Label><Input value={ferrailleurForm.raisonSociale || ""} onChange={(e) => setFerrailleurForm({ ...ferrailleurForm, raisonSociale: e.target.value })} /></Col>
                    <Col md={4}><Label>ICE</Label><Input value={ferrailleurForm.ice || ""} onChange={(e) => setFerrailleurForm({ ...ferrailleurForm, ice: e.target.value })} /></Col>
                    <Col md={4}><Label>Téléphone</Label><Input value={ferrailleurForm.telephone || ""} onChange={(e) => setFerrailleurForm({ ...ferrailleurForm, telephone: e.target.value })} /></Col>
                    <Col md={4}><Label>Ville</Label><Input value={ferrailleurForm.ville || ""} onChange={(e) => setFerrailleurForm({ ...ferrailleurForm, ville: e.target.value })} /></Col>
                    <Col xs={12}><Label>Email</Label><Input type="email" value={ferrailleurForm.email || ""} onChange={(e) => setFerrailleurForm({ ...ferrailleurForm, email: e.target.value })} /></Col>
                    <Col xs={12}>
                      <Label>Groupes</Label>
                      <div className="choice-grid">
                        {groupes.map((groupe) => (
                          <button type="button" key={groupe.id} className={(ferrailleurForm.groupeIds || []).includes(groupe.id) ? "choice-pill active" : "choice-pill"} onClick={() => toggleGroupForFerrailleur(groupe.id)}>
                            {groupe.nom} <span>{groupe.categorie}</span>
                          </button>
                        ))}
                      </div>
                    </Col>
                    <Col xs={12}><Button color="primary" type="submit">Enregistrer férailleur</Button></Col>
                  </Row>
                </form>
              </CardBody>
            </Card>

            <Card>
              <CardBody>
                <h4 className="mb-3">Férailleurs</h4>
                <div className="stack-list">
                  {ferrailleurs.map((ferrailleur) => (
                    <div className="stack-row" key={ferrailleur.id}>
                      <div>
                        <strong>{ferrailleur.nom}</strong>
                        <span>{[ferrailleur.telephone, ferrailleur.ville, (ferrailleur.groupeNoms || []).join(", ")].filter(Boolean).join(" - ") || "Coordonnées non renseignées"}</span>
                      </div>
                      <div className="d-flex gap-2 align-items-center">
                        <Badge color={ferrailleur.actif ? "success" : "secondary"}>{ferrailleur.actif ? "Actif" : "Inactif"}</Badge>
                        <Button size="sm" color="light" onClick={() => setFerrailleurForm({ ...ferrailleur, groupeIds: ferrailleur.groupeIds || [] })}>Modifier</Button>
                        <Button size="sm" color="light" className="text-danger" onClick={async () => { await deleteFerrailleur(ferrailleur.id); loadData(); }}>Supprimer</Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default ReseauPage;
